import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import styles from './DeleteAccountModal.module.css'

interface DeleteAccountModalProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  isDeleting: boolean
  error: string
}

export default function DeleteAccountModal({
  isOpen,
  onConfirm,
  onCancel,
  isDeleting,
  error,
}: DeleteAccountModalProps) {
  const { t } = useTranslation()
  const [typedValue, setTypedValue] = useState('')

  // Reset the typed confirmation whenever the modal is closed.
  useEffect(() => {
    if (!isOpen) {
      setTypedValue('')
    }
  }, [isOpen])

  if (!isOpen) return null

  const canConfirm = typedValue.toLowerCase() === 'delete' && !isDeleting

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!isDeleting && e.target === e.currentTarget) onCancel()
  }

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
    >
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onCancel}
          disabled={isDeleting}
          aria-label={t('close')}
        >
          ×
        </button>

        <h2 id="delete-account-title" className={styles.title}>
          {t('delete_account_modal_title')}
        </h2>

        <p className={styles.warning}>
          {t('delete_account_modal_warning')}
        </p>

        <div className={styles.confirmField}>
          <label className={styles.confirmLabel} htmlFor="delete-confirm-input">
            {t('delete_account_type_to_confirm')}
          </label>
          <input
            id="delete-confirm-input"
            type="text"
            className={styles.confirmInput}
            value={typedValue}
            onChange={(e) => setTypedValue(e.target.value)}
            placeholder={t('delete_account_confirm_placeholder')}
            disabled={isDeleting}
            autoComplete="off"
          />
        </div>

        {error && <p className={styles.errorMsg}>{error}</p>}

        <div className={styles.buttonRow}>
          <button
            type="button"
            className={styles.deleteConfirmBtn}
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {isDeleting ? t('deleting') : t('delete_account_confirm_btn')}
          </button>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={isDeleting}
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}
